import Vue from "vue";
import { Module } from "vuex";

import { api } from "@/util/axios";
import { FAQ, Page } from "@/types";

type FAQState = {
  faqPage: Page<FAQ>;
};

const faq: Module<FAQState, unknown> = {
  namespaced: true,
  state: () => ({
    faqPage: {
      docs: [],
      limit: 0,
      page: 0,
      pages: 0,
      sort: "",
      total: 0,
    },
  }),
  getters: {
    getFAQ: (state) => (faqId: string) =>
      state.faqPage.docs.find((f) => f._id === faqId),
    faqPage: (state) => state.faqPage,
  },
  mutations: {
    ADD_FAQ: (state, _faq) => {
      let idx = -1;
      state.faqPage.docs.map((f, i) => {
        if (f._id === _faq._id) idx = i;
      });
      if (idx === -1) state.faqPage.docs.push(_faq);
      else Vue.set(state.faqPage.docs, idx, _faq);
    },
    SET_FAQ_PAGE: (state, list) => {
      state.faqPage = list;
    },
    REMOVE_FAQ(state, faq) {
      let idx = -1;
      state.faqPage.docs.map((f, i) => {
        if (f._id === faq._id) idx = i;
      });
      if (idx > -1) state.faqPage.docs.splice(idx, 1);
    },
  },
  actions: {
    fetchFAQ(context, params: { businessId: string; faqId: string }) {
      api
        .get(`/v1/faq?faqId=${params.faqId}`)
        .then((response) => {
          context.commit("ADD_FAQ", response.data.faq);
        })
        .catch((error) => {
          context.dispatch(
            "setToast",
            {
              title: "Request failed!",
              type: "error",
              text: error.response?.data?.error?.message,
            },
            { root: true }
          );
        });
    },

    fetchFAQList(
      context,
      params: {
        businessId: string;
        categoryId?: string;
        page?: number;
        limit?: number;
        q?: string;
        sort?: string;
        _status?: string;
      }
    ) {
      const {
        businessId,
        categoryId,
        page = 1,
        limit = 50,
        q,
        sort = "order",
        _status = "active",
      } = params;

      let queryParams = `?businessId=${businessId}&page=${page}&limit=${limit}`;
      if (categoryId) queryParams += `&categoryId=${categoryId}`;
      if (q) queryParams += `&q=${q}`;
      if (sort) queryParams += `&sort=${sort}`;
      if (_status) queryParams += `&_status=${_status}`;

      api
        .get(`/v1/faq${queryParams}`)
        .then((response) => {
          context.commit("SET_FAQ_PAGE", response.data.faqPage);
        })
        .catch((error) => {
          context.dispatch(
            "setToast",
            {
              title: "Request failed!",
              type: "error",
              text:
                error.response?.data?.error?.message || "Failed to load FAQs",
            },
            { root: true }
          );
        });
    },

    async createFAQ(context, payload) {
      // Ensure we have the proper business field
      const requestPayload = {
        ...payload,
        business: payload.business || payload.businessId,
      };
      if (requestPayload.businessId) {
        delete requestPayload.businessId;
      }

      try {
        const response = await api.post(`/v1/faq`, requestPayload);
        context.commit("ADD_FAQ", response.data.faq);
        context.dispatch(
          "setToast",
          {
            title: "Success",
            type: "success",
            text: "FAQ created successfully",
          },
          { root: true }
        );
        return response.data.faq;
      } catch (error: any) {
        context.dispatch(
          "setToast",
          {
            title: "Request failed!",
            type: "error",
            text:
              error.response?.data?.error?.message || "Failed to create FAQ",
          },
          { root: true }
        );
        throw error;
      }
    },

    async updateFAQ(context, params: { faqId: string; data: Partial<FAQ> }) {
      try {
        const response = await api.put(`/v1/faq/${params.faqId}`, params.data);
        context.commit("ADD_FAQ", response.data.faq);
        context.dispatch(
          "setToast",
          {
            title: "Success",
            type: "success",
            text: "FAQ updated successfully",
          },
          { root: true }
        );
        return response.data.faq;
      } catch (error: any) {
        context.dispatch(
          "setToast",
          {
            title: "Request failed!",
            type: "error",
            text:
              error.response?.data?.error?.message || "Failed to update FAQ",
          },
          { root: true }
        );
        throw error;
      }
    },

    deleteFAQ(context, params: { faqId: string }) {
      api
        .delete(`/v1/faq/${params.faqId}`)
        .then((response) => {
          context.commit("REMOVE_FAQ", response.data.faq);
          context.dispatch(
            "setToast",
            {
              title: "Success",
              type: "success",
              text: "FAQ deleted successfully",
            },
            { root: true }
          );
        })
        .catch((error) => {
          context.dispatch(
            "setToast",
            {
              title: "Request failed!",
              type: "error",
              text:
                error.response?.data?.error?.message || "Failed to delete FAQ",
            },
            { root: true }
          );
        });
    },
  },
};

export default faq;
